import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FIELDS = [
    { key: "company_name", label: "Company name" },
    { key: "company_phone", label: "Company phone" },
    { key: "company_email", label: "Company email", type: "email" },
    { key: "company_address", label: "Company address" },
    { key: "service_area", label: "Service area" },
];

export default function Settings() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [form, setForm] = useState(null);
    const [err, setErr] = useState("");
    const [savedMsg, setSavedMsg] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/settings")
            .then((r) => setForm(r.data))
            .catch((e) => setErr(apiErrorMessage(e)));
    }, []);

    if (!form) {
        return (
            <div className="page-shell">
                <p className="text-muted">Loading settings…</p>
            </div>
        );
    }

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSavedMsg("");
        try {
            await api.put("/admin/settings", form);
            setSavedMsg("Settings saved.");
            setTimeout(() => setSavedMsg(""), 2400);
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Settings</p>
                <h1 className="h1 mt-3" data-testid="settings-title">
                    Company &amp; preferences
                </h1>
                <p className="text-muted mt-2 max-w-[640px]">
                    These details power the marketing site footer, transactional emails,
                    and lead notifications.
                </p>
            </header>

            <form
                onSubmit={save}
                className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8"
                data-testid="settings-form"
            >
                <div className="lg:col-span-2 brand-card fade-up">
                    <p className="eyebrow">Company info</p>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {FIELDS.map((f) => (
                            <div key={f.key} className={f.key === "company_address" || f.key === "service_area" ? "sm:col-span-2" : ""}>
                                <label className="field-label" htmlFor={f.key}>
                                    {f.label}
                                </label>
                                <input
                                    id={f.key}
                                    type={f.type || "text"}
                                    className="field-input"
                                    value={form[f.key] || ""}
                                    disabled={!isAdmin}
                                    onChange={(e) =>
                                        setForm({ ...form, [f.key]: e.target.value })
                                    }
                                    data-testid={`settings-${f.key}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="brand-card fade-up delay-1">
                    <p className="eyebrow">Notifications</p>
                    <div className="mt-5 flex flex-col gap-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!form.notify_email_on_new_lead}
                                disabled={!isAdmin}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        notify_email_on_new_lead: e.target.checked,
                                    })
                                }
                                data-testid="settings-notify-email"
                            />
                            <span>
                                <span className="font-display font-bold text-sm block">
                                    Email me on new leads
                                </span>
                                <span className="text-xs text-muted">
                                    Sends an alert when the marketing form is submitted (requires
                                    Postmark setup).
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!form.notify_sms_on_new_lead}
                                disabled={!isAdmin}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        notify_sms_on_new_lead: e.target.checked,
                                    })
                                }
                                data-testid="settings-notify-sms"
                            />
                            <span>
                                <span className="font-display font-bold text-sm block">
                                    SMS me on new leads
                                </span>
                                <span className="text-xs text-muted">
                                    Requires Twilio. Off by default.
                                </span>
                            </span>
                        </label>
                    </div>
                </div>

                <div className="lg:col-span-3 flex items-center gap-3">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!isAdmin || saving}
                        data-testid="settings-save-btn"
                    >
                        <Save size={14} />
                        {saving ? "Saving…" : "Save settings"}
                    </button>
                    {savedMsg && (
                        <span
                            className="text-sm"
                            style={{ color: "var(--brand-success)" }}
                            data-testid="settings-saved-msg"
                        >
                            {savedMsg}
                        </span>
                    )}
                    {!isAdmin && (
                        <span className="text-sm text-muted">
                            Only admins can edit company settings.
                        </span>
                    )}
                    {err && (
                        <span className="field-error" data-testid="settings-error">
                            {err}
                        </span>
                    )}
                </div>
            </form>
        </div>
    );
}
